import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";
import { useNavigate } from "react-router-dom";
import Icon from "components/Elements/Icon";
import {
	HistoryCaptions,
	HistoryCustomCaption,
	HistoryEntityCaptions,
	HistoryFieldLabels,
	HistoryInfo,
	HistoryProperty,
	HistoryPropertyCode,
	RICH_TEXT_FIELDS,
} from "helpers/constants";
import LinkText from "./LinkText";
import {
	historyFinalData,
	htmlToPlainText,
	isoDateWithoutTimeZone,
	formatAMPM,
} from "helpers/utils";
import { changeDate } from "helpers/date";
import { getClientUserId } from "services/users/userSites";
import { CircularProgress } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Roles from "helpers/roles";

const useStyles = makeStyles()((theme) => ({
	Wrapper: {
		padding: "15px",
		display: "flex",
	},
	iconContainer: {
		marginTop: "10px",
	},
	textContainer: {
		padding: "0px",
		marginLeft: "8px",
	},
	dateText: {
		fontStyle: "italic",
		color: "#808080",
		fontSize: "16px",
	},
	rm: {
		margin: 0,
		padding: 0,
	},
	mainText: {
		fontSize: "16px",
	},
	titleText: {
		padding: 0,
		margin: "5px 0px 0px 0px",
	},
	list: {
		padding: "5px 0px 0px 25px",
		margin: "0px",
	},
	linkText: {
		textDecoration: "underline #1880e8",
		marginTop: "8px",
		textDecorationThickness: "2px",
		color: "#1880e8",
		"&:hover": {
			cursor: "pointer",
		},
	},
	editedText: {
		fontWeight: "400",
	},
	link: {
		marginBottom: "3px",
	},
	image: {
		marginTop: "10px",
		display: "block",
		marginRight: "auto",
		height: "60px",
		width: "60px",
		objectFit: "contain",
	},
}));

function SideBarElement({
	data,
	OnAddItemClick,
	IsLoading,
	setLoading,
	origin,
	statuses,
}) {
	const { t } = useTranslation();
	const { customCaptions, application } =
		JSON.parse(sessionStorage.getItem("me")) ||
		JSON.parse(localStorage.getItem("me"));

	const { classes, cx } = useStyles();
	const navigate = useNavigate();

	const { position, role } = JSON.parse(localStorage.getItem("me") || "{}");

	if (IsLoading) return <CircularProgress />;

	return (
		<div className={classes.Wrapper}>
			<div>
				{HistoryInfo[data.auditType]?.icon
					? Icon({
							name: `${HistoryInfo[data.auditType].icon}`,
							height: "35px",
							width: "36px",
					  })
					: Icon({ name: "historyupdate", height: "35px", width: "36px" })}
			</div>
			<div className={classes.textContainer}>
				<p className={cx(classes.dateText, classes.rm)}>
					{data?.dateTime
						? `${changeDate(data.dateTime)} ${formatAMPM(
								new Date(data.dateTime + "Z")
						  )}`
						: ""}
				</p>
				<p className={cx(classes.mainText, classes.titleText)}>
					<LinkText
						isLink={position?.userAccess !== "N"}
						text={data?.user}
						onClick={async () => {
							setLoading(true);
							let userId = undefined;
							if (role === Roles.siteUser) {
								const userData = await getClientUserId(data?.userID);
								userId = userData?.data?.id;
							} else {
								userId = data?.userID;
							}

							setLoading(false);
							navigate(`/app/users/${userId}`);
						}}
					/>{" "}
					{data.auditType === 4 ? (
						<span>
							<span style={{ fontWeight: "bold" }}>{t("label.historyRead")} </span>
							{t("label.this")} {customCaptions?.defect || "Defect"}
						</span>
					) : (
						<>
							<span style={{ fontWeight: "bold" }}>
								{HistoryInfo[data.auditType]?.type}{" "}
							</span>
							<span>
								{HistoryEntityCaptions(customCaptions)?.[data.table] || ""}
							</span>
						</>
					)}
				</p>
				{data?.url ? (
					<img alt="history-detail" src={data.url} className={classes.image} />
				) : (
					<>
						{data.auditType === 4 ? (
							data?.serviceName ? (
								<div
									className={cx(classes.mainText, classes.list)}
									style={{ listStyle: "none", paddingLeft: 0 }}
								>
									<span className={classes.editedText}>
										{t("label.via")} {customCaptions?.serviceWorkOrder || "Service"}:{" "}
									</span>
									{data?.serviceID ? (
										<span
											className={classes.linkText}
											onClick={() => navigate(`/app/services/${data.serviceID}`)}
										>
											{data.serviceName}
										</span>
									) : (
										<> {data.serviceName}</>
									)}
								</div>
							) : null
						) : (
							<ul className={cx(classes.mainText, classes.list)}>
								{data.auditType === 2 ? (
							<>
								<li className={classes.link}>
									{t("label.changedColon")}{" "}
									<span className={classes.editedText}>
										{HistoryCustomCaption(
											origin,
											customCaptions,
											application?.showParts
										)?.[data?.propertyName] ||
											HistoryFieldLabels()?.[data?.propertyName] ||
											data?.propertyName}
									</span>
								</li>

								<li className={classes.link}>
									{t("label.fromColon")}{" "}
									<span className={classes.editedText}>
										{data?.table === 70 || RICH_TEXT_FIELDS.includes(data?.propertyName)
											? htmlToPlainText(data?.oldValue)
											: historyFinalData({
												value: data?.oldValue,
												statuses,
												data,
												customCaptions,
												origin,
											})}
									</span>
								</li>
								<li>
									{t("label.toColon")}{" "}
									<span className={classes.editedText}>
										{data?.table === 70 || RICH_TEXT_FIELDS.includes(data?.propertyName)
											? htmlToPlainText(data?.newValue)
											: historyFinalData({
												value: data?.newValue,
												statuses,
												data,
												customCaptions,
												origin,
											})}
									</span>
								</li>
							</>
						) : (
							<li
								className={cx({
									[classes.linkText]: data.auditType === 1,
								})}
								onClick={() => {
									if (data.auditType !== 1) return;
									OnAddItemClick && OnAddItemClick(data?.keyValue);
								}}
							>
								{data.auditType === 3
									? htmlToPlainText(data?.name)
									: htmlToPlainText(data?.newValue || data?.name)}
							</li>
						)}
							</ul>
						)}
					</>
				)}
			</div>
		</div>
	);
}

export default SideBarElement;
